import api from "./api";

/**
 * Client service for managing client CRUD operations
 */
export const clientService = {
  /**
   * Get all clients with pagination and filters
   * @param {Object} params - Query parameters
   * @param {number} params.page - Page number (default: 1)
   * @param {number} params.limit - Items per page (default: 10)
   * @param {boolean|null|undefined} params.is_active - true=active only, false=inactive only, null=all (chief-admin "All" tab). Omit for default active-only.
   * @param {string[]} [params.created_by] - Filter by creator user IDs (stored on clients.created_by)
   * @param {string[]} [params.updated_by] - Filter by last updater user IDs (stored on clients.updated_by)
   * @param {string} [params.duration_from] - Legacy: inclusive range; matches if created_at OR updated_at falls in range (only if created/updated ranges omitted)
   * @param {string} [params.duration_to] - Legacy inclusive end date
   * @param {string} [params.created_at_from] - Filter by created_at date (YYYY-MM-DD)
   * @param {string} [params.created_at_to] - Filter by created_at date (YYYY-MM-DD)
   * @param {string} [params.updated_at_from] - Filter by updated_at date (YYYY-MM-DD)
   * @param {string} [params.updated_at_to] - Filter by updated_at date (YYYY-MM-DD)
   * @param {Object} [params.filters] - e.g. { client_type: ['Production','TEST'] } (legacy prod/PROD/production accepted by API)
   * @param {string|string[]} [params.client_type] - Merged with filters.client_type on the server
   * @returns {Promise} Response with clients list
   */
  getClients: async (params = {}) => {
    const { signal, ...paramsRest } = params;
    const searchRaw = paramsRest.search_query;
    const search_query =
      typeof searchRaw === "string" ? searchRaw.trim() : (searchRaw ?? "");

    const requestData = {
      page: paramsRest.page || 1,
      limit: paramsRest.limit || 10,
      sort_by: paramsRest.sort_by,
      sort_order: paramsRest.sort_order,
      search_query,
      search_type: paramsRest.search_type || "",
    };

    // Default: active clients only. Pass is_active: null (All tab) to search active+inactive.
    if (Object.prototype.hasOwnProperty.call(paramsRest, "is_active")) {
      requestData.is_active = paramsRest.is_active;
    } else {
      requestData.is_active = true;
    }

    const listFilterKeys = [
      "created_by",
      "updated_by",
      "duration_from",
      "duration_to",
      "created_at_from",
      "created_at_to",
      "updated_at_from",
      "updated_at_to",
    ];
    listFilterKeys.forEach((key) => {
      if (!Object.prototype.hasOwnProperty.call(params, key)) return;
      const v = params[key];
      if (v === undefined || v === null || v === "") return;
      if (
        (key === "created_by" || key === "updated_by") &&
        Array.isArray(v) &&
        v.length === 0
      )
        return;
      requestData[key] = v;
    });

    if (
      Object.prototype.hasOwnProperty.call(paramsRest, "client_type") &&
      paramsRest.client_type
    ) {
      requestData.client_type = paramsRest.client_type;
    }

    if (
      paramsRest.filters &&
      typeof paramsRest.filters === "object" &&
      !Array.isArray(paramsRest.filters) &&
      Object.keys(paramsRest.filters).length > 0
    ) {
      requestData.filters = paramsRest.filters;
    }

    const response =
      signal != null
        ? await api.post("/admin/clients/list", requestData, { signal })
        : await api.post("/admin/clients/list", requestData);
    return response.data;
  },

  /**
   * Get client by ID
   * @param {string} id - Client ID
   * @returns {Promise} Response with client data
   */
  getClientById: async (id) => {
    const response = await api.post("/admin/clients/get", {
      client_id: id,
    });
    return response.data;
  },

  /**
   * Create a new client
   * @param {Object} clientData - Client data
   * @param {string} clientData.client_name - Client name
   * @returns {Promise} Response with created client
   */
  createClient: async (clientData) => {
    const response = await api.post("/admin/clients/create", clientData);
    return response.data;
  },

  /**
   * Update existing client
   * @param {string} id - Client ID
   * @param {Object} clientData - Updated client data
   * @returns {Promise} Response with updated client
   */
  updateClient: async (id, clientData) => {
    const response = await api.put("/admin/clients/update", {
      client_id: id,
      ...clientData,
    });
    return response.data;
  },

  /**
   * Delete client (soft delete)
   * @param {string} client_id - Client ID
   * @returns {Promise} Response confirmation
   */
  deleteClient: async (client_id) => {
    const response = await api.patch(`/admin/clients/delete`, {
      client_id: client_id,
    });
    return response.data;
  },

  /**
   * Deactivate client (set is_active to false)
   * @param {string} client_id - Client ID
   * @returns {Promise} Response confirmation
   */
  deactivateClient: async (client_id) => {
    const response = await api.patch(`/admin/clients/deactivate`, {
      client_id: client_id,
    });
    return response.data;
  },

  /**
   * Activate client (set is_active to true)
   * @param {string} client_id - Client ID
   * @returns {Promise} Response confirmation
   */
  activateClient: async (client_id) => {
    const response = await api.patch(`/admin/clients/activate`, {
      client_id: client_id,
    });
    return response.data;
  },

  /**
   * Permanently delete client (soft delete all client data)
   * @param {string} client_id - Client ID
   * @returns {Promise} Response confirmation
   */
  permanentDeleteClient: async (client_id) => {
    const response = await api.patch(`/admin/clients/permanent-delete`, {
      client_id: client_id,
    });
    return response.data;
  },

  /**
   * Get client statistics
   * @param {string} id - Client ID
   * @returns {Promise} Response with client stats
   */
  getClientStats: async (id) => {
    const response = await api.get(`/clients/${id}/stats`);
    return response.data;
  },

  /**
   * Get active clients for dropdown (alphabetical order)
   * @returns {Promise} Response with clients list
   */
  getClientsDropdown: async () => {
    const response = await api.post("/admin/clients/dropdown");
    return response.data;
  },

  /**
   * Get client SSO settings
   * @param {string} clientId - Client ID
   * @returns {Promise} Response with SSO settings
   */
  getClientSSOSettings: async (clientId) => {
    const response = await api.get(
      `/admin/clients/sso/settings?client_id=${clientId}`,
    );
    return response.data;
  },

  /**
   * Update client SSO settings
   * @param {string} clientId - Client ID
   * @param {Object} settings - SSO settings
   * @param {boolean} settings.sso_enabled - Enable/disable SSO
   * @param {string} settings.sso_provider - SSO provider (GOOGLE, AZURE_AD, SAML, OKTA)
   * @param {boolean} settings.sso_only_login - Require SSO-only login
   * @param {string[]} settings.allowed_domains - Allowed email domains
   * @returns {Promise} Response with updated settings
   */
  updateClientSSOSettings: async (clientId, settings) => {
    const response = await api.put(
      `/admin/clients/sso/settings?client_id=${clientId}`,
      settings,
    );
    return response.data;
  },

  /**
   * WIZARD FLOW METHODS
   * These methods support the 3-step client creation wizard
   */

  /**
   * Step 1: Create draft client
   * @param {Object} clientData - Client data
   * @param {string} clientData.client_name - Client name (required)
   * @param {string} clientData.client_description - Client description (optional)
   * @returns {Promise} Response with created draft client
   */
  createDraftClient: async (clientData) => {
    const response = await api.post("/admin/clients/draft", clientData);
    return response.data;
  },

  /**
   * Validate client name uniqueness
   * @param {string} client_name - Client name to validate
   * @param {string} client_id - Optional client ID to exclude from check
   * @returns {Promise} Response with validation result
   */
  validateClientName: async (client_name, client_id = null) => {
    const response = await api.post("/admin/clients/validate-name", {
      client_name,
      client_id,
    });
    return response.data;
  },

  /**
   * Step 2: Create admin user for draft client
   * @param {Object} userData - User data
   * @param {string} userData.client_id - Client ID (required)
   * @param {string} userData.full_name - Full name (required)
   * @param {string} userData.email - Email (required)
   * @param {string} userData.password - Password (required)
   * @returns {Promise} Response with created user
   */
  createAdminUserForDraft: async (userData) => {
    const response = await api.post("/admin/clients/admin-user", userData);
    return response.data;
  },

  /**
   * Step 3: Set CLIENT_CONFIGURATION permissions for draft client
   * @param {Object} configData - Configuration data
   * @param {string} configData.client_id - Client ID (required)
   * @param {Object} configData.permissions - Permissions object (required)
   * @returns {Promise} Response with updated permissions
   */
  setClientConfigurationForDraft: async (configData) => {
    const response = await api.post("/admin/clients/configuration", configData);
    return response.data;
  },

  /**
   * Final Step: Activate draft client (complete wizard)
   * @param {string} client_id - Client ID (required)
   * @returns {Promise} Response with activated client
   */
  activateDraftClient: async (client_id) => {
    const response = await api.post("/admin/clients/activate", { client_id });
    return response.data;
  },

  /**
   * Complete client wizard - Create client with all details in one transaction
   * @param {Object} wizardData - Complete wizard data
   * @param {string} wizardData.client_name - Client name (required)
   * @param {string} wizardData.client_description - Client description (optional)
   * @param {Object} wizardData.admin_user - Admin user data (required)
   * @param {string} wizardData.admin_user.full_name - Full name (required)
   * @param {string} wizardData.admin_user.email - Email (required)
   * @param {Object} wizardData.permissions - CLIENT_CONFIGURATION permissions (required)
   * @returns {Promise} Response with created client
   */
  completeClientWizard: async (wizardData) => {
    const response = await api.post(
      "/admin/clients/complete-wizard",
      wizardData,
    );
    return response.data;
  },
};
